import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { productSchema } from '../schemas/productSchema'
import { Upload, Trash2, ChevronUp, ChevronDown, AlertCircle, Info } from 'lucide-react'
import toast from 'react-hot-toast'
import LoadingButton from './LoadingButton'
import { extractFieldErrors } from '../utils/errorHandler'
import { generateSKU } from '../utils/skuGenerator'

interface ProductImage {
  id?: string
  file?: File
  preview: string
  isMain?: boolean
  isExisting?: boolean
  imageUrl?: string
}

interface ProductFormProps {
  initialData?: any
  categories: any[]
  onSubmit: (formData: FormData) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
}

const FormInput = ({
  label,
  name,
  type = 'text',
  placeholder,
  required,
  helper,
  readOnly = false,
  register,
  errors,
  isSubmitting,
  isLoading,
  ...props
}: any) => (
  <div>
    <div className="flex items-center gap-2 mb-2">
      <label className="block text-sm font-semibold text-[#6D3A0D]">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {helper && (
        <div title={helper} className="text-gray-400 cursor-help">
          <Info size={14} />
        </div>
      )}
    </div>
    <input
      type={type}
      placeholder={placeholder}
      disabled={isSubmitting || isLoading || readOnly}
      readOnly={readOnly}
      className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D3A0D] ${readOnly ? 'bg-gray-50 text-gray-600' : ''
        } ${errors[name] ? 'border-red-500 bg-red-50' : 'border-[#F6EEE7]'
        }`}
      {...register(name)}
      {...props}
    />
    {helper && !errors[name] && (
      <p className="text-xs text-gray-500 mt-1">{helper}</p>
    )}
    {errors[name] && (
      <div className="flex items-center gap-1 mt-1 text-sm text-red-600">
        <AlertCircle size={16} />
        {String(errors[name]?.message)}
      </div>
    )}
  </div>
)

const FormTextarea = ({
  label,
  name,
  placeholder,
  required,
  helper,
  register,
  errors,
  isSubmitting,
  isLoading,
  ...props
}: any) => (
  <div>
    <div className="flex items-center gap-2 mb-2">
      <label className="block text-sm font-semibold text-[#6D3A0D]">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {helper && (
        <div title={helper} className="text-gray-400 cursor-help">
          <Info size={14} />
        </div>
      )}
    </div>
    <textarea
      placeholder={placeholder}
      disabled={isSubmitting || isLoading}
      className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D3A0D] ${errors[name] ? 'border-red-500 bg-red-50' : 'border-[#F6EEE7]'
        }`}
      rows={4}
      {...register(name)}
      {...props}
    />
    {helper && !errors[name] && (
      <p className="text-xs text-gray-500 mt-1">{helper}</p>
    )}
    {errors[name] && (
      <div className="flex items-center gap-1 mt-1 text-sm text-red-600">
        <AlertCircle size={16} />
        {String(errors[name]?.message)}
      </div>
    )}
  </div>
)

const FormSection = ({ title, description, children }: any) => (
  <div className="space-y-4 p-6 bg-white rounded-xl border-2 border-[#F6EEE7]">
    <div>
      <h3 className="text-lg font-semibold text-[#6D3A0D]">{title}</h3>
      {description && <p className="text-sm text-gray-600">{description}</p>}
    </div>
    <div className="space-y-4">{children}</div>
  </div>
)

export default function ProductForm({
  initialData,
  categories,
  onSubmit,
  onCancel,
  isLoading = false,
}: ProductFormProps) {
  const [images, setImages] = useState<ProductImage[]>([])
  const [dragActive, setDragActive] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
  } = useForm<any>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      name: initialData?.name || '',
      description: initialData?.description || '',
      shortDescription: initialData?.shortDescription || '',
      categoryId: initialData?.categoryId || '',
      tag: initialData?.tag || '',
      sku: initialData?.sku || '',
      price: initialData?.price ? String(initialData.price) : '',
      discountPrice: initialData?.discountPrice ? String(initialData.discountPrice) : '',
      stock: initialData?.stock ? String(initialData.stock) : '',
      lowStockThreshold: initialData?.lowStockThreshold !== undefined && initialData?.lowStockThreshold !== null ? String(initialData.lowStockThreshold) : '5',
      isFeatured: initialData?.isFeatured || false,
      metaTitle: initialData?.metaTitle || '',
      metaDescription: initialData?.metaDescription || '',
      metaKeywords: initialData?.metaKeywords || '',
      weight: initialData?.weight ? String(initialData.weight) : '0.5',
      length: initialData?.length ? String(initialData.length) : '10',
      width: initialData?.width ? String(initialData.width) : '10',
      height: initialData?.height ? String(initialData.height) : '10',
      mainImage: initialData?.mainImage || '',
    },
  })

  const productName = watch('name')
  const price = watch('price')
  const discountPrice = watch('discountPrice')

  // Auto-generate SKU when product name changes
  useEffect(() => {
    if (productName && !initialData?.sku) {
      const generatedSKU = generateSKU(productName)
      setValue('sku', generatedSKU)
    }
  }, [productName, initialData?.sku, setValue])

  // Calculate discount percentage
  const calculateDiscountPercentage = () => {
    if (price && discountPrice) {
      const priceNum = parseFloat(price)
      const discountNum = parseFloat(discountPrice)
      if (priceNum > 0 && discountNum > 0) {
        const discount = ((priceNum - discountNum) / priceNum) * 100
        return Math.round(discount)
      }
    }
    return null
  }

  const discountPercentage = calculateDiscountPercentage()

  useEffect(() => {
    if (initialData?.mainImage) {
      const existingImages: ProductImage[] = []

      // Add main image first
      existingImages.push({
        preview: initialData.mainImage,
        isMain: true,
        isExisting: true,
      })

      // Add gallery images if they exist
      if (initialData?.images && Array.isArray(initialData.images)) {
        initialData.images.forEach((img: any) => {
          const imageUrl = typeof img === 'string' ? img : img.imageUrl
          const imageId = typeof img === 'string' ? undefined : img.id
          existingImages.push({
            id: imageId,
            preview: imageUrl,
            imageUrl: imageUrl,
            isMain: false,
            isExisting: true,
          })
        })
      }

      setImages(existingImages)
    }
  }, [initialData])

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files) handleFiles(Array.from(files))
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files) {
      handleFiles(Array.from(e.dataTransfer.files))
    }
  }

  const handleFiles = (files: File[]) => {
    const validImages: ProductImage[] = []
    let validCount = 0

    files.forEach((file) => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`)
        return
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 5MB limit`)
        return
      }

      validCount++
      const reader = new FileReader()
      reader.onloadend = () => {
        validImages.push({
          file,
          preview: reader.result as string,
          isMain: images.length === 0 && validImages.length === 0,
          isExisting: false,
        })

        if (validImages.length === validCount) {
          const allImages = [...images, ...validImages]
          setImages(allImages)
          // Ensure first image is marked as main
          if (allImages.length > 0) {
            const mainImg = allImages.find((img) => img.isMain)
            if (!mainImg) {
              allImages[0].isMain = true
            }
            setValue('mainImage', allImages[0].file || allImages[0].preview)
            // Clear image validation error when a valid image is added
            clearErrors('mainImage')
          }
        }
      }
      reader.readAsDataURL(file)
    })
  }

  const removeImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index)
    if (newImages.length === 0) {
      setError('mainImage', {
        type: 'manual',
        message: 'At least one main image is required. At least one product image is required.',
      })
      setImages([])
      return
    }

    const galleryImages = newImages.filter((img) => !img.isMain)
    if (galleryImages.length === 0) {
      setError('mainImage', {
        type: 'manual',
        message: 'At least one product image is required.',
      })
      setImages(newImages)
      return
    }

    if (images[index].isMain) {
      newImages[0].isMain = true
      setValue('mainImage', newImages[0].file || newImages[0].preview)
    }
    setImages(newImages)
    clearErrors('mainImage')
  }

  const setMainImage = (index: number) => {
    const newImages = images.map((img, i) => ({
      ...img,
      isMain: i === index,
    }))
    setImages(newImages)
    setValue('mainImage', newImages[index].file || newImages[index].preview)
    clearErrors('mainImage')
  }

  const moveImage = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= images.length) return

    const newImages = [...images]
      ;[newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]]
    setImages(newImages)
  }

  const onFormSubmit = async (values: any) => {
    try {
      // Validate: must have at least one main image and one gallery image
      if (images.length === 0) {
        setError('mainImage', {
          type: 'manual',
          message: 'At least one main image is required. At least one product image is required.',
        })
        return
      }

      const mainImage = images.find((img) => img.isMain)
      if (!mainImage) {
        setError('mainImage', {
          type: 'manual',
          message: 'Main image is required.',
        })
        return
      }

      const galleryImages = images.filter((img) => !img.isMain)
      if (galleryImages.length === 0) {
        setError('mainImage', {
          type: 'manual',
          message: 'At least one product image is required.',
        })
        return
      }

      const form = new FormData()

      form.append('name', values.name)
      form.append('description', values.description)
      form.append('shortDescription', values.shortDescription || '')
      form.append('categoryId', values.categoryId)
      if (values.tag) form.append('tag', values.tag)
      form.append('sku', values.sku)
      form.append('price', String(values.price))
      if (values.discountPrice) form.append('discountPrice', String(values.discountPrice))
      form.append('stock', String(values.stock))
      if (values.lowStockThreshold) form.append('lowStockThreshold', String(values.lowStockThreshold))
      form.append('isFeatured', String(values.isFeatured))
      form.append('metaTitle', values.metaTitle || '')
      form.append('metaDescription', values.metaDescription || '')
      form.append('metaKeywords', values.metaKeywords || '')
      if (values.weight !== undefined && values.weight !== '') form.append('weight', String(values.weight))
      if (values.length !== undefined && values.length !== '') form.append('length', String(values.length))
      if (values.width !== undefined && values.width !== '') form.append('width', String(values.width))
      if (values.height !== undefined && values.height !== '') form.append('height', String(values.height))

      // Always send main image - either new file or existing URL
      if (mainImage.file instanceof File) {
        form.append('mainImage', mainImage.file)
      } else if (mainImage.preview && mainImage.isExisting) {
        form.append('mainImage', mainImage.preview)
      }

      // Send IDs of existing gallery images to keep (for deletion handling)
      const existingGalleryImageIds = galleryImages
        .filter((img) => img.isExisting && img.id)
        .map((img) => img.id)

      if (existingGalleryImageIds.length > 0) {
        form.append('keepImageIds', existingGalleryImageIds.join(','))
      }

      // Send new gallery images
      galleryImages.forEach((img) => {
        if (img.file instanceof File) {
          form.append('images', img.file)
        }
      })

      await onSubmit(form)
    } catch (error: any) {
      const fieldErrors = extractFieldErrors(error)
      if (Object.keys(fieldErrors).length > 0) {
        Object.entries(fieldErrors).forEach(([key, message]) => {
          setError(key, {
            type: 'server',
            message: String(message),
          })
        })
      }
    }
  }


  return (
    <form onSubmit={handleSubmit(onFormSubmit)} noValidate className="space-y-6">
      {/* Basic Information */}
      <FormSection
        title="Basic Information"
        description="Core product details that customers will see"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Product Name"
            name="name"
            placeholder="e.g., Hair Nourishing Serum"
            required
            helper="Clear product name customers will search for"
            register={register}
            errors={errors}
            isSubmitting={isSubmitting}
            isLoading={isLoading}
          />

          <FormInput
            label="Product ID (SKU)"
            name="sku"
            placeholder="Auto-generated"
            readOnly={true}
            helper="Automatically generated based on product name"
            register={register}
            errors={errors}
            isSubmitting={isSubmitting}
            isLoading={isLoading}
          />

          <div>
            <label className="block text-sm font-semibold text-[#6D3A0D] mb-2">
              Category <span className="text-red-500">*</span>
            </label>
            {categories.length === 0 ? (
              <div className="w-full px-4 py-2 border-2 border-orange-300 bg-orange-50 rounded-lg text-sm text-orange-700">
                ⚠️ No categories available. Create one first in the Categories section.
              </div>
            ) : (
              <>
                <select
                  {...register('categoryId')}
                  disabled={isSubmitting || isLoading}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D3A0D] ${errors.categoryId ? 'border-red-500 bg-red-50' : 'border-[#F6EEE7]'
                    }`}
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                {errors.categoryId && (
                  <div className="flex items-center gap-1 mt-1 text-sm text-red-600">
                    <AlertCircle size={16} />
                    {String(errors.categoryId?.message)}
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-[#6D3A0D]">
                Product Tag
              </label>
              <div title="Optional: Mark special product status to highlight it" className="text-gray-400 cursor-help">
                <Info size={14} />
              </div>
            </div>
            <select
              {...register('tag')}
              disabled={isSubmitting || isLoading}
              className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D3A0D] ${errors.tag ? 'border-red-500 bg-red-50' : 'border-[#F6EEE7]'
                }`}
            >
              <option value="">No Special Tag</option>
              <option value="NEW">🆕 New Product</option>
              <option value="BEST_SELLER">⭐ Best Seller</option>
              <option value="FEATURED">💎 Featured</option>
              <option value="TRENDING">📈 Trending</option>
              <option value="DISCOUNT">🏷️ On Discount</option>
            </select>
            {!errors.tag && (
              <p className="text-xs text-gray-500 mt-1">Optional: Mark special product status to highlight it</p>
            )}
            {errors.tag && (
              <div className="flex items-center gap-1 mt-1 text-sm text-red-600">
                <AlertCircle size={16} />
                {String(errors.tag?.message)}
              </div>
            )}
          </div>
        </div>

        <FormTextarea
          label="Short Summary"
          name="shortDescription"
          placeholder="Brief overview (shown in product lists) - max 500 characters"
          helper="A quick description customers see before clicking on the product"
          register={register}
          errors={errors}
          isSubmitting={isSubmitting}
          isLoading={isLoading}
        />

        <FormTextarea
          label="Product Description"
          name="description"
          placeholder="Detailed description including benefits, ingredients, usage instructions - min 10 characters"
          required
          helper="Complete product details - customers read this when viewing the product"
          register={register}
          errors={errors}
          isSubmitting={isSubmitting}
          isLoading={isLoading}
        />
      </FormSection>

      {/* Pricing */}
      <FormSection title="Pricing" description="Set product price and discounts">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Actual Price"
            name="price"
            type="number"
            step="0.01"
            placeholder="Enter product price"
            required
            helper="The regular selling price"
            register={register}
            errors={errors}
            isSubmitting={isSubmitting}
            isLoading={isLoading}
          />

          <FormInput
            label="Discount Price"
            name="discountPrice"
            type="number"
            step="0.01"
            placeholder="Enter discounted price"
            helper="Sale price - must be less than actual price"
            register={register}
            errors={errors}
            isSubmitting={isSubmitting}
            isLoading={isLoading}
          />
        </div>

        {discountPercentage !== null && (
          <div className="p-3 bg-green-50 rounded-lg border-l-4 border-green-500">
            <p className="text-sm text-green-700">
              💰 <strong>Discount:</strong> {discountPercentage}% off
            </p>
          </div>
        )}
      </FormSection>

      {/* Inventory */}
      <FormSection title="Inventory" description="Stock levels and alerts">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Available Stock"
            name="stock"
            type="number"
            placeholder="Enter available stock"
            required
            helper="Current quantity available for sale"
            register={register}
            errors={errors}
            isSubmitting={isSubmitting}
            isLoading={isLoading}
          />

          <FormInput
            label="Low Stock Alert Level"
            name="lowStockThreshold"
            type="number"
            placeholder="Enter low stock alert level"
            helper="Alert when stock falls below this number"
            register={register}
            errors={errors}
            isSubmitting={isSubmitting}
            isLoading={isLoading}
          />
        </div>
      </FormSection>

      {/* Shipping & Package Dimensions */}
      <FormSection title="Shipping & Package Dimensions" description="Dimensions and weight used for dynamic Shiprocket logistics calculations">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <FormInput
            label="Weight (KG)"
            name="weight"
            type="number"
            step="0.01"
            placeholder="Enter weight in kg"
            helper="Package weight in kilograms"
            register={register}
            errors={errors}
            isSubmitting={isSubmitting}
            isLoading={isLoading}
          />
          <FormInput
            label="Length (CM)"
            name="length"
            type="number"
            step="0.1"
            placeholder="Enter length in cm"
            helper="Package length in cm"
            register={register}
            errors={errors}
            isSubmitting={isSubmitting}
            isLoading={isLoading}
          />
          <FormInput
            label="Width (CM)"
            name="width"
            type="number"
            step="0.1"
            placeholder="Enter width in cm"
            helper="Package width in cm"
            register={register}
            errors={errors}
            isSubmitting={isSubmitting}
            isLoading={isLoading}
          />
          <FormInput
            label="Height (CM)"
            name="height"
            type="number"
            step="0.1"
            placeholder="Enter height in cm"
            helper="Package height in cm"
            register={register}
            errors={errors}
            isSubmitting={isSubmitting}
            isLoading={isLoading}
          />
        </div>
      </FormSection>

      {/* Featured Product */}
      <div className="p-6 bg-white rounded-xl border-2 border-[#F6EEE7]">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="isFeatured"
            {...register('isFeatured')}
            disabled={isSubmitting || isLoading}
            className="w-4 h-4 rounded"
          />
          <label htmlFor="isFeatured" className="text-sm font-semibold text-[#6D3A0D] cursor-pointer">
            Featured Product
          </label>
          <p className="text-xs text-gray-500">(Show prominently on homepage)</p>
        </div>
      </div>

      {/* Product Images */}
      <FormSection title="Product Images" description="Upload at least one cover image and one gallery image">
        <div>
          <label className="block text-sm font-semibold text-[#6D3A0D] mb-2">
            Upload Images <span className="text-red-500">*</span>
          </label>

          {/* Drag and Drop Zone */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive ? 'border-[#6D3A0D] bg-[#F6EEE7]' : 'border-[#F6EEE7] hover:border-[#6D3A0D]'
              } ${isSubmitting || isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <Upload className="mx-auto mb-2 text-[#6D3A0D]" size={32} />
            <p className="text-sm font-semibold text-[#6D3A0D] mb-1">Drag and drop images here</p>
            <p className="text-xs text-gray-600 mb-4">or</p>
            <input
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              onChange={handleImageSelect}
              className="hidden"
              id="image-input"
              disabled={isSubmitting || isLoading}
            />
            <label
              htmlFor="image-input"
              className="inline-block px-4 py-2 bg-[#6D3A0D] text-white rounded-lg text-sm font-semibold cursor-pointer hover:bg-[#5D2A0D] disabled:opacity-50"
            >
              Select Files
            </label>
            <p className="text-xs text-gray-500 mt-4">JPG, PNG, WebP up to 5MB each (Max 10 images)</p>
          </div>

          {errors.mainImage && (
            <div className="flex items-center gap-1 mt-2 text-sm text-red-600">
              <AlertCircle size={16} />
              {String(errors.mainImage?.message)}
            </div>
          )}

          {/* Image Gallery */}
          {images.length > 0 && (
            <div className="mt-4 space-y-3">
              {images.map((image, index) => (
                <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <img
                    src={image.preview}
                    alt={`Preview ${index + 1}`}
                    className="w-16 h-16 object-cover rounded"
                  />

                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#6D3A0D]">
                      {image.file?.name || `Image ${index + 1}`}
                    </p>
                    {image.isMain && (
                      <p className="text-xs text-green-600 font-medium">✓ Cover Image</p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {!image.isMain && (
                      <button
                        type="button"
                        onClick={() => setMainImage(index)}
                        className="px-3 py-1 text-xs bg-[#6D3A0D] text-white rounded hover:bg-[#5D2A0D]"
                        disabled={isSubmitting || isLoading}
                      >
                        Set as Cover
                      </button>
                    )}

                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => moveImage(index, 'up')}
                        className="p-1 hover:bg-white rounded disabled:opacity-50"
                        disabled={isSubmitting || isLoading}
                        title="Move up"
                      >
                        <ChevronUp size={16} className="text-[#6D3A0D]" />
                      </button>
                    )}

                    {index < images.length - 1 && (
                      <button
                        type="button"
                        onClick={() => moveImage(index, 'down')}
                        className="p-1 hover:bg-white rounded disabled:opacity-50"
                        disabled={isSubmitting || isLoading}
                        title="Move down"
                      >
                        <ChevronDown size={16} className="text-[#6D3A0D]" />
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="p-1 hover:bg-white rounded text-red-600 disabled:opacity-50"
                      disabled={isSubmitting || isLoading}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </FormSection>

      {/* SEO Settings */}
      <FormSection
        title="Search Engine Optimization (SEO)"
        description="Help customers find your product through search engines (optional)"
      >
        <FormInput
          label="SEO Title"
          name="metaTitle"
          placeholder="Unique title for search results - max 60 characters"
          helper="Appears in Google search results. Keep it concise and include main keywords."
          register={register}
          errors={errors}
          isSubmitting={isSubmitting}
          isLoading={isLoading}
        />
        <FormInput
          label="SEO Description"
          name="metaDescription"
          placeholder="Summary for search results - max 160 characters"
          helper="The preview text shown under your product in search results. Make it compelling!"
          register={register}
          errors={errors}
          isSubmitting={isSubmitting}
          isLoading={isLoading}
        />
        <FormInput
          label="SEO Keywords"
          name="metaKeywords"
          placeholder="hair serum, nourishing, natural, organic"
          helper="Comma-separated keywords customers might search for"
          register={register}
          errors={errors}
          isSubmitting={isSubmitting}
          isLoading={isLoading}
        />
      </FormSection>

      {/* Form Actions */}
      <div className="flex gap-4 pt-6 sticky bottom-0 bg-white py-4 border-t-2 border-[#F6EEE7]">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting || isLoading}
          className="flex-1 px-4 py-2 border-2 border-[#F6EEE7] text-[#6D3A0D] rounded-lg font-semibold hover:bg-[#F6EEE7] transition disabled:opacity-50"
        >
          Cancel
        </button>
        <LoadingButton
          type="submit"
          state={isSubmitting || isLoading ? 'loading' : 'idle'}
          variant="primary"
          size="md"
          className="flex-1 bg-[#6D3A0D] hover:bg-[#5D2A0D]"
          loadingText="Uploading..."
        >
          {initialData ? 'Update Product' : 'Create Product'}
        </LoadingButton>
      </div>
    </form>
  )
}
